import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, uploadImage } from "../../lib/api";

export const EMPTY_PRODUCT = { name: "", category: "Tops", price: "", description: "", image: "", images: [], featured: false, sizes: ["XS", "S", "M", "L", "XL"], inventory: {} };
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const ease = [0.22, 1, 0.36, 1];

const Field = ({ label, children }) => (
  <div><label className="label block mb-2">{label}</label>{children}</div>
);

const PhotoUploaders = ({ form, setField, setEditing }) => {
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const handlePrimary = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPrimary(true);
    try { const url = await uploadImage(f); setField("image", url); toast.success("Photo uploaded"); }
    catch { toast.error("Upload failed. Try again."); }
    finally { setUploadingPrimary(false); e.target.value = ""; }
  };

  const handleGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      setEditing((ed) => ({ ...ed, form: { ...ed.form, images: [...(ed.form.images || []).filter(Boolean), ...urls] } }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added`);
    } catch { toast.error("Upload failed. Try again."); }
    finally { setUploadingGallery(false); e.target.value = ""; }
  };

  const removeGallery = (i) => setEditing((ed) => ({ ...ed, form: { ...ed.form, images: (ed.form.images || []).filter((_, idx) => idx !== i) } }));

  return (
    <>
      <div>
        <label className="label block mb-2">Primary Photo</label>
        {form.image
          ? <img src={form.image} alt="preview" data-testid="form-image-preview" className="w-full aspect-[3/4] object-cover bg-[#E2E4E8] mb-3" />
          : <div className="w-full aspect-[3/4] bg-[#E2E4E8] mb-3 flex items-center justify-center label text-[#8A8F99]">No photo yet</div>}
        <label data-testid="upload-primary-label" className="flex items-center justify-center gap-2 border-2 border-dashed border-[#0B0E14] h-16 cursor-pointer hover:bg-black/5 transition-colors label">
          <Upload className="w-4 h-4" /> {uploadingPrimary ? "Uploading…" : "Upload / replace photo"}
          <input type="file" accept="image/*" data-testid="upload-primary" className="hidden" onChange={handlePrimary} />
        </label>
        <input data-testid="form-image" value={form.image} onChange={(e) => setField("image", e.target.value)} className="fld mt-3" placeholder="…or paste an image URL" />
      </div>

      <div>
        <label className="label block mb-2">Gallery photos</label>
        {!!(form.images || []).filter(Boolean).length && (
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="gallery-previews">
            {(form.images || []).filter(Boolean).map((src, i) => (
              <div key={src} className="relative aspect-[3/4]">
                <img src={src} alt={`gallery ${i + 1}`} className="w-full h-full object-cover bg-[#E2E4E8]" />
                <button type="button" onClick={() => removeGallery(i)} className="absolute top-1 right-1 bg-[#0B0E14] text-white w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
        <label data-testid="upload-gallery-label" className="flex items-center justify-center gap-2 border-2 border-dashed border-[#0B0E14] h-14 cursor-pointer hover:bg-black/5 transition-colors label">
          <Upload className="w-4 h-4" /> {uploadingGallery ? "Uploading…" : "Add gallery photos"}
          <input type="file" accept="image/*" multiple data-testid="upload-gallery" className="hidden" onChange={handleGallery} />
        </label>
      </div>
    </>
  );
};

const InventoryFields = ({ form, setEditing }) => {
  const setInv = (size, v) => setEditing((e) => ({ ...e, form: { ...e.form, inventory: { ...(e.form.inventory || {}), [size]: v } } }));
  const invTotal = Object.values(form.inventory || {}).reduce((s, v) => s + (parseInt(v, 10) || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="label">Inventory by size</label>
        <span className="label text-[#8A8F99]" data-testid="inventory-total">Total: {invTotal}</span>
      </div>
      <p className="label text-[#8A8F99] normal-case tracking-normal mb-3" style={{ textTransform: "none" }}>Leave a size blank if you don't carry it. Sizes with a number become the sizes shoppers can pick.</p>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {SIZES.map((s) => (
          <div key={s} className="flex flex-col items-center gap-1">
            <span className="label text-[10px]">{s}</span>
            <input
              type="number" min="0" inputMode="numeric"
              data-testid={`inv-${s}`}
              value={form.inventory?.[s] ?? ""}
              onChange={(e) => setInv(s, e.target.value)}
              className="w-full h-11 text-center border border-[#0B0E14] bg-transparent outline-none focus:ring-1 focus:ring-[#0B0E14]"
              placeholder="—"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export const ProductForm = ({ editing, setEditing, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    if (!editing.form.image) { toast.error("Add a primary photo first"); return; }
    setSaving(true);
    const payload = { ...editing.form, price: parseFloat(editing.form.price) };
    const extra = (editing.form.images || []).filter((u) => u && u.trim());
    payload.images = [editing.form.image, ...extra].filter((u, i, a) => u && a.indexOf(u) === i);
    const inv = {};
    Object.entries(editing.form.inventory || {}).forEach(([s, v]) => {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && n >= 0) inv[s] = n;
    });
    payload.inventory = inv;
    try {
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing.id ? "Piece updated" : "Piece added");
      setEditing(null);
      await onSaved();
    } catch {
      toast.error("Could not save. Check the fields.");
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {editing && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)} className="fixed inset-0 bg-[#0B0E14]/40 z-[60]" />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.5, ease }}
            data-testid="product-form" className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-[#EDEEF1] z-[70] border-l border-[#D4D7DD] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[#D4D7DD] sticky top-0 bg-[#EDEEF1]">
              <span className="label">{editing.id ? "Edit Piece" : "New Piece"}</span>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-6">
              <PhotoUploaders form={editing.form} setField={setField} setEditing={setEditing} />
              <Field label="Name"><input data-testid="form-name" required value={editing.form.name} onChange={(e) => setField("name", e.target.value)} className="fld" /></Field>
              <Field label="Category">
                <select data-testid="form-category" value={editing.form.category} onChange={(e) => setField("category", e.target.value)} className="fld">
                  <option>Tops</option><option>Bottoms</option><option>Outerwear</option><option>Headwear</option><option>Accessories</option>
                </select>
              </Field>
              <Field label="Price (USD)"><input data-testid="form-price" required type="number" step="0.01" value={editing.form.price} onChange={(e) => setField("price", e.target.value)} className="fld" /></Field>
              <InventoryFields form={editing.form} setEditing={setEditing} />
              <Field label="Description"><textarea data-testid="form-description" rows={4} value={editing.form.description} onChange={(e) => setField("description", e.target.value)} className="fld resize-none" /></Field>
              <label className="flex items-center gap-3 label cursor-pointer">
                <input data-testid="form-featured" type="checkbox" checked={editing.form.featured} onChange={(e) => setField("featured", e.target.checked)} className="w-4 h-4 accent-[#0B0E14]" />
                Feature on homepage
              </label>
              <button data-testid="save-product" disabled={saving} className="w-full bg-[#0B0E14] text-[#EDEEF1] h-14 label hover:bg-[#1B2233] transition-colors disabled:opacity-50">
                {saving ? "Saving…" : editing.id ? "Update Piece" : "Add Piece"}
              </button>
            </form>
            <style>{`.fld{width:100%;height:48px;padding:0 14px;background:transparent;border:1px solid #0B0E14;outline:none}.fld:focus{box-shadow:0 0 0 1px #0B0E14}textarea.fld{height:auto;padding:12px 14px}`}</style>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
